import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import BrandLogo from "@/components/feature/BrandLogo";

const navLinks = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "综艺", to: "/category/variety" },
  { label: "关于我们", to: "/about" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    setMobileOpen(false);
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
    setKeyword("");
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full bg-background-50/96 backdrop-blur transition-colors ${
        scrolled ? "border-b border-background-300/80" : "border-b border-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 items-center gap-6">
          <BrandLogo />

          <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                className={({ isActive }) =>
                  `relative rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? "text-primary-800"
                      : "text-foreground-700 hover:bg-background-100 hover:text-primary-700"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {link.label}
                    <span
                      className={`absolute inset-x-3 -bottom-[3px] h-0.5 rounded-full bg-accent-500 transition-opacity ${
                        isActive ? "opacity-100" : "opacity-0"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto hidden items-center gap-2.5 lg:flex">
            <form onSubmit={submitSearch} className="relative" role="search">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[15px] text-foreground-400" />
              <input
                type="search"
                name="q"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索电影、电视剧、综艺"
                aria-label="搜索视频"
                className="h-10 w-48 rounded-md border border-background-300 bg-background-100 pl-9 pr-16 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:ring-2 focus:ring-primary-200/70 xl:w-64"
              />
              <button
                type="submit"
                className="absolute right-1 top-1 h-8 cursor-pointer rounded bg-primary-800 px-3 text-xs font-semibold whitespace-nowrap text-background-50 transition-colors hover:bg-primary-900"
              >
                搜索
              </button>
            </form>
            <Link
              to="/about"
              className="hidden h-10 items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-3.5 text-sm font-medium text-primary-800 transition-colors hover:border-primary-300 hover:bg-primary-50 xl:flex"
            >
              <i className="ri-mail-line text-base" />
              联系我们
            </Link>
          </div>

          <button
            type="button"
            aria-label={mobileOpen ? "关闭菜单" : "打开菜单"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((prev) => !prev)}
            className="ml-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-primary-800 lg:hidden"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
          </button>
        </div>

        <form onSubmit={submitSearch} className="relative pb-3 lg:hidden" role="search">
          <i className="ri-search-line pointer-events-none absolute left-3 top-[13px] text-[15px] text-foreground-400" />
          <input
            type="search"
            name="q"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜索电影、电视剧、综艺"
            aria-label="搜索视频"
            className="h-10 w-full rounded-md border border-background-300 bg-background-100 pl-9 pr-20 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
          />
          <button
            type="submit"
            className="absolute right-1 top-1 h-8 cursor-pointer rounded bg-primary-800 px-3 text-xs font-semibold whitespace-nowrap text-background-50"
          >
            搜索
          </button>
        </form>
      </div>

      {mobileOpen && (
        <div className="border-t border-background-300/70 bg-background-50 px-4 pb-5 pt-3 lg:hidden">
          <nav className="flex flex-col" aria-label="移动端导航">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center justify-between rounded-md px-3 py-3 text-sm font-medium ${
                    isActive ? "bg-primary-50 text-primary-800" : "text-foreground-800"
                  }`
                }
              >
                {link.label}
                <i className="ri-arrow-right-s-line text-lg text-foreground-400" />
              </NavLink>
            ))}
          </nav>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {[
              { label: "电影", to: "/category/movie" },
              { label: "电视剧", to: "/category/tv" },
              { label: "综艺", to: "/category/variety" },
            ].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                className="rounded-md bg-background-100 px-3 py-2.5 text-center text-xs font-medium text-primary-800"
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}